<template>
  <template v-if="hasGifts">
    <!-- Обычные подарки -->
    <BasketRulesGiftSummaryItem
        v-for="gift in regularGifts"
        :key="`regular-${gift.ruleId}`"
        :gift="gift"
        :is-additional="false"
        @click="openGiftDialog"
    />

    <!-- Дополнительные подарки -->
    <BasketRulesGiftSummaryItem
        v-for="gift in additionalGifts"
        :key="`additional-${gift.ruleId}`"
        :gift="gift"
        :is-additional="true"
        @click="openGiftDialog"
    />
  </template>
</template>

<script setup lang="ts">
import {useImager} from '@/core/store/imager';
import {useBasketRulesStore} from '@/modules/shop/store/basket-rules';
import {storeToRefs} from 'pinia';
import {computed} from 'vue';
import {useRouter} from 'vue-router';
import BasketRulesGiftSummaryItem, {type GiftDisplay} from './BasketRulesGiftSummaryItem.vue';

/**
 * Компонент для отображения подарков в Summary форме заказа
 *
 * Отображает как обычные (regular), так и дополнительные (additional) подарки
 * в стиле c-line с причиной слева и названием + фото справа
 *
 * При клике открывается диалог выбора подарков
 */

const basketRulesStore = useBasketRulesStore();
const {imageResolve} = useImager();
const router = useRouter();

const {
  selectedGift,
  selectedGiftAdditional,
  activeRegularGiftRulesStates,
  activeAdditionalGiftRulesStates,
} = storeToRefs(basketRulesStore);

/**
 * Обычные подарки для отображения
 */
const regularGifts = computed<GiftDisplay[]>(() => {
  const states = activeRegularGiftRulesStates.value;
  if (states.length === 0) return [];

  // Берем первое правило (они отсортированы по приоритету)
  const state = states[0];
  const rule = state.rule;

  // Получаем выбранный подарок
  const selectedGiftId = selectedGift.value;
  if (!selectedGiftId) return [];

  const product = basketRulesStore.getGiftProduct(selectedGiftId);
  if (!product) return [];

  return [{
    ruleId: rule.ID,
    reason: rule.CAPTION || rule.NAME,
    productName: product.NAME,
    imageSrc: product.IMAGE?.SRC ? imageResolve(product.IMAGE.SRC, 's-1') : null,
  }];
});

/**
 * Дополнительные подарки для отображения
 */
const additionalGifts = computed<GiftDisplay[]>(() => {
  const states = activeAdditionalGiftRulesStates.value;
  if (states.length === 0) return [];

  // Берем первое правило (они отсортированы по приоритету)
  const state = states[0];
  const rule = state.rule;

  // Получаем выбранный подарок
  const selectedGiftId = selectedGiftAdditional.value;
  if (!selectedGiftId) return [];

  const product = basketRulesStore.getGiftProduct(selectedGiftId);
  if (!product) return [];

  return [{
    ruleId: rule.ID,
    reason: rule.CAPTION || rule.NAME,
    productName: product.NAME,
    imageSrc: product.IMAGE?.SRC ? imageResolve(product.IMAGE.SRC, 's-1') : null,
  }];
});

/**
 * Есть ли подарки для отображения
 */
const hasGifts = computed(() => {
  return regularGifts.value.length > 0 || additionalGifts.value.length > 0;
});

/**
 * Открывает диалог выбора подарков
 */
function openGiftDialog() {
  router.push('basket/rules-gifts');
}
</script>

<style scoped lang="scss">
.c-gifts-summary {
  // Контейнер для всех подарков
}
</style>
